import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
const SEO = {
  title: 'Login | Zano',
  description: 'Sign in to your Zano account to discover creators and access the influencer marketplace or manage your roster.',
  ogTitle: 'Login | Zano',
  ogDescription: 'Sign in to your Zano account to discover creators and access the influencer marketplace or manage your roster.'
};
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Prototype: no real auth
  };
  return <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 pt-16" data-id="element-2677" data-mp="1kuor07z8vwxs">
      {/* Form side */}
      <div className="flex items-center justify-center px-6 py-16" data-id="element-2678" data-mp="2aiucdm42e7jh">
        <div className="w-full max-w-sm" data-id="element-2679" data-mp="2ej4fmr0h2jun">
          <div className="flex items-center mb-8" data-id="element-2680" data-mp="2jhfooziy0qbt">
            <img src="https://cdn.magicpatterns.com/uploads/gbY7Dn8gGgHtYDAdDbncPd/zano_logo_full1.png" alt="Zano" className="h-8 w-auto" data-id="element-2681" data-mp="2jgo5ko6j8l2f" />
          </div>

          <h1 className="text-2xl font-bold text-zano-charcoal mb-2" data-id="element-2682" data-mp="2tf3v4mpwvm9t">
            Welcome back
          </h1>
          <p className="text-gray-500 mb-8" data-id="element-2683" data-mp="2xfe3g616eoru">Log in to your Zano workspace.</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2684" data-mp="3298gwrq6u7ih">
            <div data-id="element-2685" data-mp="377jr53keak18">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2686" data-mp="375yw7cu64z2u">
                Email
              </label>
              <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zano-pink/40 focus:border-zano-pink" data-id="element-2687" data-mp="3h4eovk8uggeg" />
            </div>
            <div data-id="element-2688" data-mp="qln5ita9vgni">
              <div className="flex items-center justify-between mb-1.5" data-id="element-2689" data-mp="qm4ztkddjkss">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-2690" data-mp="1o7aqmi1a2cjq">
                  Password
                </label>
                <a href="#" className="text-xs font-medium text-zano-pink hover:underline" data-id="element-2691" data-mp="1ehhffi51rzg0">
                  Forgot password?
                </a>
              </div>
              <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zano-pink/40 focus:border-zano-pink" data-id="element-2692" data-mp="270enec9sl5ik" />
            </div>
            <Button variant="primary" type="submit" className="w-full justify-center py-2.5" data-id="element-2693" data-mp="1cu0q6bufz34u">
              Log in
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6" data-id="element-2694" data-mp="25ixutc5qd1f4">
            <div className="h-px flex-1 bg-gray-200" data-id="element-2695" data-mp="2aed6pyk6gn8w" />
            <span className="text-xs text-gray-400" data-id="element-2696" data-mp="1vqs405r7sq1i">or</span>
            <div className="h-px flex-1 bg-gray-200" data-id="element-2697" data-mp="1vq8v1ejjlbzm" />
          </div>

          <Button variant="secondary" className="w-full justify-center py-2.5 !text-gray-700 !border-gray-300 hover:!bg-gray-50" data-id="element-2698" data-mp="20otn2qon4pcz">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-4 h-4 mr-2" data-id="element-2699" data-mp="2t660hv72mcho" />
            Continue with Google
          </Button>

          <p className="text-center text-sm text-gray-500 mt-8" data-id="element-2700" data-mp="csm5z3qfs45m">
            New to Zano?{' '}
            <Link to="/pricing" className="font-medium text-zano-pink hover:underline" data-id="element-2701" data-mp="rhi9cnhjsszw">
              Request a demo
            </Link>
          </p>
        </div>
      </div>

      {/* Brand panel */}
      <div className="hidden lg:flex relative items-center justify-center bg-gradient-to-br from-zano-blush via-white to-zano-lavender overflow-hidden" data-id="element-2702" data-mp="3uy8vcbtizgn8">
        <div className="absolute top-10 right-10 w-72 h-72 bg-zano-pink/10 rounded-full blur-3xl" data-id="element-2703" data-mp="7sraemvpuqe8" />
        <div className="relative z-10 max-w-md px-10" data-id="element-2704" data-mp="3ro0si49hqlg">
          <h2 className="text-3xl font-bold text-zano-charcoal mb-4 leading-tight" data-id="element-2705" data-mp="3q4hsjr811fz">
            The Creator Asset OS for modern teams
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-2706" data-mp="1f748jaxx787w">
            Source, license, and activate creator content from one connected
            workflow.
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-float p-5" data-id="element-2707" data-mp="bxrp2tn1eb6b">
            <div className="flex items-center gap-3 mb-4" data-id="element-2708" data-mp="gsxcjww37xwi">
              <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=80&q=80" alt="" className="w-11 h-11 rounded-full object-cover" data-id="element-2709" data-mp="ltlc56ydtn2c" />
              <div data-id="element-2710" data-mp="3pcekn5cazlpn">
                <p className="font-semibold text-gray-900 text-sm" data-id="element-2711" data-mp="3p8qrw8eon3bz">
                  Elena Rodriguez
                </p>
                <p className="text-xs text-gray-500" data-id="element-2712" data-mp="2641j3tlpea4">
                  Select Mgmt • Live profile
                </p>
              </div>
            </div>
            <div className="space-y-2" data-id="element-2713" data-mp="6xvnf11tavtx">
              {['Media kit up to date', 'Rights tracked', 'Brief generated'].map(t => <div key={t} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-2714" data-mp="3ajunclbu48g4">
                  <CheckCircle2 size={15} className="text-green-500" data-id="element-2715" data-mp="2y3bfyo1viprm" /> {t}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
};